"use client";
import { logger } from "@/utils/logger";

import { useState, useRef, useEffect } from "react";
import { FloatingInput, FloatingTextarea } from "@/components/ui/FloatingInput";
import { Check, ArrowRight, ArrowLeft, Send } from "lucide-react";

const categories = [
  "Enterprise Software / SaaS",
  "Cloud & Infrastructure (AWS/Azure/GCP)",
  "Supply Chain & Raw Materials",
  "Facilities Operations",
  "Consulting & Agency Rosters",
  "Other Commercial Spend",
];

const spends = [
  "Under $500,000 / year",
  "$500,000 to $2 Million / year",
  "$2 Million to $10 Million / year",
  "Over $10 Million / year",
];

const urgencies = [
  "Immediate (Contract renewal in < 30 days)",
  "Active planning (Renewal in next 3–6 months)",
  "General optimization (Ongoing contract audit)",
  "Exploratory benchmarking",
];

export default function QuoteMe() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const isMounted = useRef(true);
  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  const [selections, setSelections] = useState({
    category: "",
    spend: "",
    urgency: "",
    name: "",
    email: "",
    phone: "",
    company: "",
    details: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleNext = () => {
    setDirection(1);
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    setDirection(-1);
    if (step > 1) setStep(step - 1);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!selections.name.trim()) newErrors.name = "Name is required";
    if (!selections.company.trim()) newErrors.company = "Company is required";
    if (!selections.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selections.email)) newErrors.email = "Valid corporate email is required";
    if (!selections.phone.trim() || !/^[\d\s\+\-\(\)]{7,20}$/.test(selections.phone)) newErrors.phone = "Valid contact number is required";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/public/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: selections.name,
          email: selections.email,
          phone: selections.phone,
          companyName: selections.company,
          serviceRequested: `${selections.category} | ${selections.spend} | ${selections.urgency}`,
          message: selections.details || "No additional details provided",
        })
      });
      if (res.ok) {
        if (isMounted.current) setSubmitted(true);
      } else {
        logger.error("Failed to submit quote request");
      }
    } catch (err) {
      logger.error(err);
    } finally {
      if (isMounted.current) setIsSubmitting(false);
    }
  };

  const isNextDisabled =
    (step === 1 && !selections.category) ||
    (step === 2 && !selections.spend) ||
    (step === 3 && !selections.urgency);

  const heroContentRef = useRef<HTMLDivElement>(null);
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const successContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (heroContentRef.current) {
      import("gsap").then(({ default: gsap }) => {
        if (heroContentRef.current) {
          gsap.fromTo(heroContentRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (stepContainerRef.current && !submitted) {
      const xOffset = direction > 0 ? 48 : -48;
      import("gsap").then(({ default: gsap }) => {
        if (stepContainerRef.current && !submitted) {
          gsap.fromTo(stepContainerRef.current, 
            { opacity: 0, x: xOffset },
            { opacity: 1, x: 0, duration: 0.4, ease: "power2.out", overwrite: "auto" }
          );
        }
      });
    }
  }, [step, direction, submitted]);

  useEffect(() => {
    import("gsap").then(({ default: gsap }) => {
      gsap.to(`.progress-bar`, {
        width: (i: any, el: any) => {
          const index = parseInt(el.dataset.index);
          return index <= step ? 20 : 14;
        },
        backgroundColor: (i: any, el: any) => {
          const index = parseInt(el.dataset.index);
          return index <= step ? "#db0004" : "#e5e5e5";
        },
        duration: 0.3,
        ease: "power2.out"
      });
    });
  }, [step]);

  useEffect(() => {
    if (submitted && successContainerRef.current) {
      import("gsap").then(({ default: gsap }) => {
        if (successContainerRef.current) {
          gsap.fromTo(successContainerRef.current, 
            { opacity: 0, scale: 0.96 }, 
            { opacity: 1, scale: 1, duration: 0.45, ease: "power2.out" }
          );
          gsap.fromTo(".success-check", { scale: 0 }, { scale: 1, duration: 0.6, delay: 0.2, ease: "elastic.out(1, 0.5)" });
          gsap.fromTo(".success-details", { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, delay: 0.35 });
        }
      });
    }
  }, [submitted]);

  return (
    <div className="min-h-screen bg-brand-offwhite">
      {/* 1. HERO SECTION */}
      <section className="bg-brand-white border-b border-brand-border/60 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div
            ref={heroContentRef}
            className="space-y-4 opacity-0"
          >
            <p className="editorial-label">Interactive Quote Assessment</p>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-brand-black">
              Configure Your Category Analysis
            </h1>
            <p className="font-sans text-xs sm:text-sm text-brand-muted max-w-lg mx-auto">
              Provide details of your active commercial agreements, and our analysts will calculate a fee quote and benchmark audit strategy.
            </p>
          </div>
        </div>
      </section>

      {/* 2. MULTI-STEP WIZARD PANEL */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto bg-brand-white border border-brand-border/60 p-8 md:p-10 relative overflow-hidden">

            {submitted ? (
              <div
                ref={successContainerRef}
                className="py-12 text-center space-y-6 opacity-0"
              >
                <div
                  className="success-check w-16 h-16 bg-brand-red/5 border border-brand-red/20 text-brand-red flex items-center justify-center rounded-full mx-auto"
                >
                  <Check className="w-8 h-8" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-brand-black">
                  Category Assessment Initialized
                </h3>
                <p className="text-xs text-brand-muted max-w-sm mx-auto leading-relaxed">
                  We have received your configuration details. A senior category advisor will review the benchmarks and contact you to set up a secure file share.
                </p>

                <div
                  className="success-details border border-brand-border/60 p-4 max-w-md mx-auto text-left text-xs bg-brand-offwhite space-y-2 font-medium text-brand-muted opacity-0"
                >
                  <p className="text-[10px] font-bold text-brand-black uppercase tracking-wider">Summary of request:</p>
                  <p>• Category: {selections.category}</p>
                  <p>• Volume: {selections.spend}</p>
                  <p>• Urgency: {selections.urgency}</p>
                </div>

                <button
                  onClick={() => {
                    setSubmitted(false);
                    setStep(1);
                    setDirection(1);
                    setSelections({ category: "", spend: "", urgency: "", name: "", email: "", phone: "", company: "", details: "" });
                    setErrors({});
                  }}
                  className="text-[10px] font-sans font-bold uppercase tracking-widest text-brand-red hover:underline"
                >
                  Configure Another Category
                </button>
              </div>
            ) : (
              <div>
                {/* Progress Header */}
                <div className="mb-10 flex items-center justify-between border-b border-brand-border/60 pb-6 text-[10px] font-sans font-bold uppercase tracking-widest text-brand-muted">
                  <span>Step {step} of 4</span>
                  <div className="flex gap-1.5 items-center">
                    {[1, 2, 3, 4].map((i) => (
                      <span
                        key={i}
                        data-index={i}
                        className="progress-bar h-1 block w-[14px] bg-[#e5e5e5]"
                      />
                    ))}
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div ref={stepContainerRef} className="opacity-0">
                    {/* STEP 1: CATEGORY SELECTION */}
                    {step === 1 && (
                      <div
                        className="space-y-6"
                      >
                        <h3 className="font-serif text-xl font-bold text-brand-black">
                          Which spending category requires optimization?
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                          {categories.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setSelections({ ...selections, category: c })}
                              className={`group w-full text-left px-5 py-4 border text-xs font-sans font-semibold flex items-center justify-between select-none transition-all duration-200 hover:translate-x-1 active:scale-[0.99] ${
                                selections.category === c
                                  ? "border-brand-red bg-brand-red/5 text-brand-red translate-x-1"
                                  : "border-brand-border bg-brand-offwhite text-brand-black hover:border-brand-black"
                              }`}
                            >
                              <span>{c}</span>
                              <span className={`transition-all duration-200 ${selections.category === c ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
                                <Check className="w-4 h-4 text-brand-red shrink-0" />
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* STEP 2: SPEND TIER */}
                    {step === 2 && (
                      <div
                        className="space-y-6"
                      >
                        <h3 className="font-serif text-xl font-bold text-brand-black">
                          What is the approximate annual expenditure in this category?
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                          {spends.map((s) => (
                            <button
                              key={s}
                              type="button"
                              onClick={() => setSelections({ ...selections, spend: s })}
                              className={`group w-full text-left px-5 py-4 border text-xs font-sans font-semibold flex items-center justify-between select-none transition-all duration-200 hover:translate-x-1 active:scale-[0.99] ${
                                selections.spend === s
                                  ? "border-brand-red bg-brand-red/5 text-brand-red translate-x-1"
                                  : "border-brand-border bg-brand-offwhite text-brand-black hover:border-brand-black"
                              }`}
                            >
                              <span>{s}</span>
                              <span className={`transition-all duration-200 ${selections.spend === s ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
                                <Check className="w-4 h-4 text-brand-red shrink-0" />
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* STEP 3: URGENCY */}
                    {step === 3 && (
                      <div
                        className="space-y-6"
                      >
                        <h3 className="font-serif text-xl font-bold text-brand-black">
                          What is the operational urgency for this category?
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                          {urgencies.map((u) => (
                            <button
                              key={u}
                              type="button"
                              onClick={() => setSelections({ ...selections, urgency: u })}
                              className={`group w-full text-left px-5 py-4 border text-xs font-sans font-semibold flex items-center justify-between select-none transition-all duration-200 hover:translate-x-1 active:scale-[0.99] ${
                                selections.urgency === u
                                  ? "border-brand-red bg-brand-red/5 text-brand-red translate-x-1"
                                  : "border-brand-border bg-brand-offwhite text-brand-black hover:border-brand-black"
                              }`}
                            >
                              <span>{u}</span>
                              <span className={`transition-all duration-200 ${selections.urgency === u ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
                                <Check className="w-4 h-4 text-brand-red shrink-0" />
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* STEP 4: CONTACT DETAILS */}
                    {step === 4 && (
                      <div
                        className="space-y-6"
                      >
                        <div className="space-y-1">
                          <h3 className="font-serif text-xl font-bold text-brand-black">
                            Submit Contact Information
                          </h3>
                          <p className="text-xs text-brand-muted">
                            We require a corporate email to benchmark rates and deliver the fee options.
                          </p>
                        </div>

                        <div className="space-y-5">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                              <FloatingInput
                                label="Your Name"
                                id="quote-name"
                                required
                                value={selections.name}
                                onChange={(v) => setSelections({ ...selections, name: v })}
                                placeholder="Jane Miller"
                              />
                              {errors.name && <span className="text-xs text-brand-red mt-1 block">{errors.name}</span>}
                            </div>
                            <div>
                              <FloatingInput
                                label="Company Name"
                                id="quote-company"
                                required
                                value={selections.company}
                                onChange={(v) => setSelections({ ...selections, company: v })}
                                placeholder="Logistics Group"
                              />
                              {errors.company && <span className="text-xs text-brand-red mt-1 block">{errors.company}</span>}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                              <FloatingInput
                                label="Corporate Email"
                                id="quote-email"
                                type="email"
                                required
                                value={selections.email}
                                onChange={(v) => setSelections({ ...selections, email: v })}
                                placeholder="jane@logistics.com"
                              />
                              {errors.email && <span className="text-xs text-brand-red mt-1 block">{errors.email}</span>}
                            </div>
                            <div>
                              <FloatingInput
                                label="Phone Number"
                                id="quote-phone"
                                type="tel"
                                required
                                value={selections.phone}
                                onChange={(v) => setSelections({ ...selections, phone: v })}
                                placeholder="+44 800 170 0216"
                              />
                              {errors.phone && <span className="text-xs text-brand-red mt-1 block">{errors.phone}</span>}
                            </div>
                          </div>

                          <FloatingTextarea
                            label="Short Category Notes (Optional)"
                            id="quote-details"
                            rows={3}
                            value={selections.details}
                            onChange={(v) => setSelections({ ...selections, details: v })}
                            placeholder="Enter active vendor names or primary negotiation objectives..."
                          />
                        </div>
                      </div>
                    )}
                    </div>

                  {/* Wizard navigation */}
                  <div className="mt-10 pt-6 border-t border-brand-border/60 flex items-center justify-between">
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center gap-2 text-[10px] font-sans font-bold uppercase tracking-widest text-brand-muted hover:text-brand-black transition-all hover:-translate-x-1 active:scale-[0.97]"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back
                      </button>
                    ) : (
                      <span />
                    )}

                    {step < 4 ? (
                      <button
                        type="button"
                        onClick={handleNext}
                        disabled={isNextDisabled}
                        className="inline-flex items-center gap-2 px-5 py-3 text-[10px] font-sans font-bold uppercase tracking-widest bg-brand-black text-brand-white border border-brand-black disabled:bg-brand-subtle disabled:border-brand-subtle disabled:text-brand-muted transition-all cursor-pointer hover:bg-brand-red hover:border-brand-red select-none active:scale-[0.97] hover:translate-x-1 disabled:hover:translate-x-0 disabled:active:scale-100"
                      >
                        Next Step
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center gap-2 px-5 py-3 text-[10px] font-sans font-bold uppercase tracking-widest bg-brand-red text-brand-white border border-brand-red disabled:bg-brand-subtle disabled:border-brand-subtle disabled:text-brand-muted transition-all cursor-pointer select-none hover:scale-[1.02] active:scale-[0.97] disabled:hover:scale-100 disabled:active:scale-100"
                      >
                        {isSubmitting ? "Submitting..." : "Submit Request"}
                        {!isSubmitting && <Send className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

        </div>
      </section>
    </div>
  );
}
